import React, { useState, useEffect, useCallback } from "react";
import { Tabs, Card, Row, Col, Spin, Table, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { 
  getCorporateReportAgewiseReport, 
  getCorporateReportRelationwiseReport, 
  getCorporateMonthwiseReport, 
  getCorporateReportSuminsuredwiseReport, 
  getCorporateReportTotalclaimReport, 
  getCorporateReportGenderwiseReport, 
  getClaimReportPaidVsOutstanding 
} from "../../Action/Actions";
// import { useSelectedYear } from "../../Context/SelectYearContext"; // --- REMOVED
import { getUserData } from "../login/LoginSlice";
import { selectFinancialYears, selectSelectedYearId } from "../../Redux/financialYearSlice"; // +++ ADDED
// import StatusReport from "./StatusReport";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, Sector, LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar } from "recharts";
import { EyeOutlined, UserOutlined, TeamOutlined, FileAddOutlined, FireOutlined } from "@ant-design/icons";

const RESTRICTED_POLICY_TYPE_IDS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];

const ClaimReport = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { module_option_ids } = location.state || {};
  const AuthData = useSelector(getUserData);
  
  // +++ ADDED: Get financial year data from Redux store
  const selectedYearId = useSelector(selectSelectedYearId);
  const financialYears = useSelector(selectFinancialYears);

  // Find the selected year's display name from the year list
  // Assuming the year object has a property like 'financial_year' for the display string '2023-2024'
  const selectedYear = financialYears.find(year => year.id === selectedYearId)?.financial_year || "Not Selected";
  
  const [activeTab, setActiveTab] = useState("");
  const [selectedPolicyNumber, setSelectedPolicyNumber] = useState(null);
  // const { selectedYear } = useSelectedYear(); // --- REMOVED
  const [ageWiseData, setAgeWiseData] = useState({});
  const [relationWiseData, setRelationWiseData] = useState([]);
  const [monthWiseData, setMonthWiseData] = useState({});
  const [sumInsuredWiseData, setSumInsuredWiseData] = useState([]);
  const [totalClaimData, setTotalClaimData] = useState([]);
  const [ClaimPaidData, setClaimPaidData] = useState([]);
  const [genderWiseData, setGenderWiseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const policyTypes = Object.keys(ageWiseData || {});
  const [activeIndex, setActiveIndex] = useState(0);
  const user = useSelector(getUserData);
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28EFF']; // Define your custom colors
  const generateShadesOfBlue = (count) => {
    return Array.from(
      { length: count },
      (_, i) => `hsl(220, 80%, ${30 + i * (50 / count)}%)`
    );
  };

  const COLORS7 = generateShadesOfBlue(5);

  useEffect(() => {
    const fetchAgeWiseReport = async () => {
      if (!AuthData?.corporate_id || !selectedYearId) {
        setLoading(false);
        setAgeWiseData({});
        setSelectedPolicyNumber(null);
        return;
      }
      try {
        setLoading(true);
        const data = await dispatch(getCorporateReportAgewiseReport(AuthData?.corporate_id));
        const { age_wise_reports } = data;
        
        // NEW: Filter out restricted policy types
        const filtered_age_wise_reports = {};
        for (const type in age_wise_reports) {
          const typeName = String(type).toLowerCase();
          if (typeName.includes('marine') || typeName.includes('fire')) {
            continue;
          }

          const policies = age_wise_reports[type] || [];
          const validPolicies = policies.filter(p => {
            const details = Array.isArray(p.policy_details) ? p.policy_details[0] : p.policy_details;
            const typeId = details?.policy_type_id || p.policy_type_id || p.policy_details?.policy_type_id;
            const isRestrictedId = RESTRICTED_POLICY_TYPE_IDS.includes(Number(typeId));
            return !isRestrictedId;
          });
          if (validPolicies.length > 0) {
            filtered_age_wise_reports[type] = validPolicies;
          }
        }

        setAgeWiseData(filtered_age_wise_reports || {});
        
        // Get policy types that actually have data (not empty arrays)
        const availableTabs = Object.keys(filtered_age_wise_reports || {}).filter(
          (tab) => filtered_age_wise_reports[tab] && filtered_age_wise_reports[tab].length > 0
        );
        
        // Use the first available type that has data
        const tabToUse = availableTabs.length > 0 ? availableTabs[0] : "";
        
        // Update activeTab to the first available type with data
        if (tabToUse && tabToUse !== activeTab) {
          setActiveTab(tabToUse);
        }
        
        const firstPolicy = age_wise_reports[tabToUse]?.[0]?.policy_details;
        setSelectedPolicyNumber(firstPolicy || null);
      } catch (err) {
        console.error("Error fetching age-wise report:", err);
        setAgeWiseData({});
        setSelectedPolicyNumber(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAgeWiseReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, AuthData?.corporate_id, selectedYearId]);

  useEffect(() => {
    if (selectedPolicyNumber) {
      setLoading(true);
      // Assuming these thunks are also updated to pull the financialYearId from the Redux state
      dispatch(getCorporateReportRelationwiseReport(selectedPolicyNumber.policy_id))
        .then((data) => {
          const relationData = [
            { name: "Employee", count: data.employee_count.employee_count, amount: data.employee_totals.employee_total_amount },
            { name: "Spouse", count: data.employee_count.spouse_count, amount: data.employee_totals.spouse_total_amount },
            { name: "Child", count: data.employee_count.child_count, amount: data.employee_totals.child_total_amount },
            { name: "Parent", count: data.employee_count.parent_count, amount: data.employee_totals.parent_total_amount },
            { name: "Parent-in-law", count: data.employee_count.parentinlaw_count, amount: data.employee_totals.parentinlaw_total_amount },
          ];
          setRelationWiseData(relationData);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.error("Error fetching relation-wise report:", err);
        });

      dispatch(getCorporateMonthwiseReport(selectedPolicyNumber.policy_id))
        .then((data) => {
          setMonthWiseData(data);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.error("Error fetching month-wise report:", err);
        });

      dispatch(getCorporateReportSuminsuredwiseReport(selectedPolicyNumber.policy_id))
        .then((data) => {
          setSumInsuredWiseData(data);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.error("Error fetching sum insured-wise report:", err);
        });

      dispatch(getCorporateReportTotalclaimReport(selectedPolicyNumber.policy_id))
        .then((data) => {
          setTotalClaimData(data);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.error("Error fetching total claim report:", err);
        });
        
      dispatch(getCorporateReportGenderwiseReport(selectedPolicyNumber.policy_id))
        .then((data) => {
          const formattedData = data
            .filter((item) => item.name !== "Total") // Exclude "Total"
            .map((item) => ({
              ...item,
              percentage: parseFloat(item.percentage), // Convert percentage to a numeric value
            }));
          setGenderWiseData(formattedData);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.error("Error fetching gender-wise report:", err);
        });
    }
  }, [dispatch, selectedPolicyNumber]);

  useEffect(() => {
    if (selectedPolicyNumber) {
      setLoading(true);
      dispatch(getClaimReportPaidVsOutstanding(selectedPolicyNumber.policy_id))
        .then((data) => {
          const formattedData = [
            {
              name: "Paid",
              cashless_amount: data.Paid.cashless_amount,
              cashless_count: data.Paid.cashless_count,
              reimbursement_amount: data.Paid.reimbursement_amount,
              reimbursement_count: data.Paid.reimbursement_count,
              total_amount: data.Paid.total_amount,
              total_count: data.Paid.total_count,
            },
            {
              name: "Outstanding",
              cashless_amount: data.Outstanding.cashless_amount,
              cashless_count: data.Outstanding.cashless_count,
              reimbursement_amount: data.Outstanding.reimbursement_amount,
              reimbursement_count: data.Outstanding.reimbursement_count,
              total_amount: data.Outstanding.total_amount,
              total_count: data.Outstanding.total_count,
            },
          ];
          setClaimPaidData(formattedData);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.error("Error fetching claim paid vs outstanding report:", err);
        });
    }
  }, [dispatch, selectedPolicyNumber]);
 

  const onPieEnter = useCallback(
    (_, index) => {
      setActiveIndex(index);
    },
    [setActiveIndex]
  );

  const renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
    } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;

    return (
      <g>
        <text x={cx} y={cy} dy={-20} textAnchor="middle" fill={fill} fontSize={25} fontWeight={800}>
          {payload.name}
        </text>
        <text x={cx} y={cy} dy={20} textAnchor="middle" fill={fill} fontSize={25}>
          {payload.count}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 5}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={innerRadius - 5}
          outerRadius={innerRadius - 3}
          fill={fill}
        />
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} fill="none" />
      </g>
    );
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.7;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={14}>
        <tspan x={x} dy="-0.5em">{`${(percent * 100).toFixed(0)}%`}</tspan>
        <tspan x={x} dy="1.2em">{name}</tspan>
      </text>
    );
  };
  
  const renderCustomizedLabel1 = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <g>
        <text x={x} y={y + 10} fill="#000" textAnchor="middle" dominantBaseline="central" fontSize={12}>
          {name} {/* Display name */}
        </text>
        <text x={x} y={y - 10} fill="#000" textAnchor="middle" dominantBaseline="central" fontSize={14}>
          {`${(percent * 100).toFixed(2)}%`} {/* Display percentage */}
        </text>
      </g>
    );
  };

  const COLORS6 = ['#afd8f8', '#edc240'];
  const columns = [
    {
      title: 'AGE',
      dataIndex: 'age',
      key: 'age',
    },
    {
      title: 'COUNT',
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: 'AMOUNT',
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: 'PERCENTAGE',
      dataIndex: 'percentage',
      key: 'percentage',
    },
  ];

  const columns2 = [
    {
      title: 'RELATION NAME',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'COUNT',
      dataIndex: 'count',
      key: 'count',
    },
    {
      title: 'AMOUNT',
      dataIndex: 'amount',
      key: 'amount',
    },
  ];

  const columns3 = [
    {
      title: "MONTH",
      dataIndex: "month",
      key: "month",
    },
    {
      title: "CASHLESS COUNT",
      dataIndex: "cashless_count",
      key: "cashless_count",
    },
    {
      title: "CASHLESS AMOUNT",
      dataIndex: "cashless_amount",
      key: "cashless_amount",
    },
    {
      title: "REIMBURSEMENT COUNT",
      dataIndex: "reimbursement_count",
      key: "reimbursement_count",
    },
    {
      title: "REIMBURSEMENT AMOUNT",
      dataIndex: "reimbursement_amount",
      key: "reimbursement_amount",
    },
    {
      title: "TOTAL COUNT",
      dataIndex: "total_count",
      key: "total_count",
    },
    {
      title: "TOTAL AMOUNT",
      dataIndex: "total_amount",
      key: "total_amount",
    },
  ];

  const columns4 = [
    {
      title: "SUM INSURED",
      dataIndex: "sum_insured_range",
      key: "sum_insured_range",
    },
    {
      title: "CASHLESS",
      children: [
        { title: "AMOUNT", dataIndex: "cashless_amount", key: "cashless_amount" },
        { title: "COUNT", dataIndex: "cashless_count", key: "cashless_count" },
      ],
    },
    {
      title: "REIMBURSEMENT",
      children: [
        { title: "AMOUNT", dataIndex: "reimbursement_amount", key: "reimbursement_amount" },
        { title: "COUNT", dataIndex: "reimbursement_count", key: "reimbursement_count" },
      ],
    },
    {
      title: "TOTAL",
      children: [
        { title: "AMOUNT", dataIndex: "total_amount", key: "total_amount" },
        { title: "COUNT", dataIndex: "total_count", key: "total_count" },
      ],
    },
  ];

  const columns5 = [
    {
      title: "CLAIM TYPE",
      dataIndex: "claim_type",
      key: "claim_type",
      
    },
    {
      title: "CLAIM AMOUNT",
      dataIndex: "claim_amount",
      key: "claim_amount",
      
    },
    {
      title: "CLAIM COUNT",
      dataIndex: "claim_count",
      key: "claim_count",
     
    },
  ];

  const columns6 = [
    {
      title: "GENDER",
      dataIndex: "name",
      key: "name",
      render: (text) => (text === "Total" ? <b style={{ color: "#7B6E64" }}>{text}</b> : text),
    },
    {
      title: "COUNT",
      dataIndex: "count",
      key: "count",
    },
    {
      title: "AMOUNT",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "PERCENTAGE",
      dataIndex: "percentage",
      key: "percentage",
      render: (value) => `${value.toFixed(2)}%`, // Format percentage to 2 decimal places
    },
  ];

  const columns1= [
    {
      title: "STATUS",
      dataIndex: "name",
      key: "name",
   
    },
    {
      title: "CASHLESS",
      children: [
        { title: "AMOUNT", dataIndex: "cashless_amount", key: "cashless_amount" },
        { title: "COUNT", dataIndex: "cashless_count", key: "cashless_count" },
      ],
    },
    {
      title: "REIMBURSEMENT",
      children: [
        { title: "AMOUNT", dataIndex: "reimbursement_amount", key: "reimbursement_amount" },
        { title: "COUNT", dataIndex: "reimbursement_count", key: "reimbursement_count" },
      ],
    },
    {
      title: "TOTAL",
      children: [
        { title: "AMOUNT", dataIndex: "total_amount", key: "total_amount" },
        { title: "COUNT", dataIndex: "total_count", key: "total_count" },
      ],
    },
  ];

  const handleTabChange = (key) => {
    setActiveTab(key);
    const firstPolicy = ageWiseData[key]?.[0]?.policy_details;
    setSelectedPolicyNumber(firstPolicy || null);
  };

  const handlePolicyNumberClick = (key) => {
    // Find the policy object based on the key (either policy_number or policy-${index})
    const policy = ageWiseData[activeTab]?.find((p, index) => {
      const policyKey = p.policy_details.policy_number || `policy-${index}`;
      return policyKey === key;
    });
    
    if (policy) {
      setSelectedPolicyNumber(policy.policy_details);
    }
  };

  const getTabIcon = (type) => {
    switch (type) {
      case "GMC":
        return <TeamOutlined style={{ marginRight: 8 }} />;
      case "GTL":
        return <UserOutlined style={{ marginRight: 8 }} />;
      case "GPA":
        return <TeamOutlined style={{ marginRight: 8 }} />;
      case "Parent policy":
        return <TeamOutlined style={{ marginRight: 8 }} />;
      case "Topup":
        return <FileAddOutlined style={{ marginRight: 8 }} />;
       case "Fire":
        return <FireOutlined style={{ marginRight: 8 }} />;
      default:
        return null;
    }
  };

  const tabItems = policyTypes?.map((type) => ({
    key: type,
    label: (
      <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        {type}
        {getTabIcon(type)}
      </span>
    ),
    children: (
      <>
        <Tabs
          activeKey={selectedPolicyNumber ? (selectedPolicyNumber.policy_number || `policy-${ageWiseData[type]?.findIndex(p => p.policy_details.policy_id === selectedPolicyNumber.policy_id)}`) : undefined}
          onChange={handlePolicyNumberClick}
          items={ageWiseData[type]?.map((policy, index) => ({
            key: policy.policy_details.policy_number || `policy-${index}`,
            label: policy.policy_details.policy_number ? (
              policy.policy_details.policy_number
            ) : (
              <span
                style={{
                  backgroundColor: "#FFFF00",
                  color: "#000000E0",
                  padding: "2px 8px",
                  fontSize: "12px",
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  borderRadius: "4px",
                  display: "inline-block",
                }}
              >
                Policy Number not issued
              </span>
            ),
          }))}
        />
        {selectedPolicyNumber ? (
          <>
            <Card>
              <Card>
                <h5>Age Wise</h5>
                {ageWiseData[type]?.find(
                  (item) => item.policy_details.policy_number === selectedPolicyNumber.policy_number
                )?.agewise_reports?.length > 0 ? (
                  <>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                      <ResponsiveContainer width={800} height={400}>
                        <PieChart>
                          <Pie
                            data={ageWiseData[type]?.find(
                              (item) => item.policy_details.policy_number === selectedPolicyNumber.policy_number
                            )?.agewise_reports.map((item) => ({
                              name: item.Age_Group,
                              count: item.Count,
                              amount: item.Amount,
                              percentage: item.Percentage,
                            }))}
                            dataKey="count"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={160}
                            labelLine={false}
                            label={(props) => renderCustomizedLabel({ ...props, name: props.name })}
                          >
                            {ageWiseData[type]?.find(
                                  (item) => item.policy_details.policy_number === selectedPolicyNumber.policy_number
                                )?.agewise_reports.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                          </Pie>
                          <Tooltip />
                          <Legend
                            layout="vertical"
                            align="right"
                            verticalAlign="middle"
                            iconSize={10}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    <Table
                      dataSource={ageWiseData[type]?.find(
                        (item) => item.policy_details.policy_number === selectedPolicyNumber.policy_number
                      )?.agewise_reports.map((item) => ({
                        age: item.Age_Group,
                        value: item.Count,
                        amount: item.Amount,
                        percentage: item.Percentage,
                      }))}
                      columns={columns}
                      pagination={false}
                      bordered
                      style={{ marginTop: 20 }}
                    />
                  </>
                ) : (
                  <h6 className="report-error" style={{ textAlign: "left", marginTop: 20 }}>
                    Record Not Found
                  </h6>
                )}
              </Card>
 
              <Card>
                <h5>Relation Wise</h5>
                {relationWiseData.length > 0 && relationWiseData.every(item => item.count === 0) ? (
                  <h6 className="report-error" style={{ textAlign: "left", marginTop: 20 }}>Record Not Found</h6>
                ) : (
                  <>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                      <ResponsiveContainer width={800} height={400}>
                        <PieChart width={800} height={400}>
                          <Pie
                            activeIndex={activeIndex}
                            activeShape={renderActiveShape}
                            data={relationWiseData}
                            cx="50%"
                            cy="50%"
                            innerRadius={120}
                            outerRadius={160}
                            dataKey="count"
                            nameKey="name"
                            onMouseEnter={onPieEnter}
                          >
                            {relationWiseData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS7[index % COLORS7.length]} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <Table
                      dataSource={relationWiseData}
                      columns={columns2}
                      pagination={false}
                      bordered
                      style={{ marginTop: 20 }}
                    />
                  </>
                )}
              </Card>

              <Card>
                <h5>Month Wise</h5>
                {Object.entries(monthWiseData).length > 0 ? (
                  <>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={Object.entries(monthWiseData).map(([month, data]) => ({ month, ...data })).filter(item => item.name !== "Total")}>
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <CartesianGrid strokeDasharray="3 3" vertical={false} horizontal={true} />
                      <Line type="monotone" dataKey="cashless_count" stroke="#6d6ef3" dot={{ r: 6 }} />
                      <Line type="monotone" dataKey="reimbursement_count" stroke="#6d6ef3" dot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
               
                  <Table
                    dataSource={Object.entries(monthWiseData).map(([month, data]) => ({ month, ...data })).filter(item => item.name !== "Total")}
                    columns={columns3}
                    pagination={false}
                    bordered
                    style={{ marginTop: 20 }}
                  />
                  </>
                ) : (
                  <h6 className="report-error" style={{ textAlign: "left", marginTop: 20 }}>Record Not Found</h6>
                )}
              </Card> 
              <Card>
                <h5>Sum Insured wise</h5>
                {sumInsuredWiseData.length > 0 &&
                sumInsuredWiseData.filter(item => item.name !== "Total").some(
                  item => item.cashless_count > 0 || item.reimbursement_count > 0
                ) ? (
                  <>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                          data={sumInsuredWiseData.filter(item => item.name !== "Total")}
                          barSize={40}
                        >
                          <XAxis dataKey="sum_insured_range" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <CartesianGrid strokeDasharray="3 3" vertical={true} horizontal={false} />
                          <Bar dataKey="cashless_count" fill="#8884d8" name="Cashless" />
                          <Bar dataKey="reimbursement_count" fill="#FF1493" name="Reimbursement" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <Table
                      dataSource={sumInsuredWiseData.filter(item => item.name !== "Total")}
                      columns={columns4}
                      pagination={false}
                      bordered
                      style={{ marginTop: 20 }}
                    />
                  </>
                ) : (
                  <h6 className="report-error" style={{ textAlign: "left", marginTop: 20 }}>
                    Record Not Found
                  </h6>
                )}
              </Card>
             
              <Card>
                <h5>Total Claims Reported</h5>
                {Array.isArray(totalClaimData) &&
                totalClaimData.filter(item => item.name !== "Total").some(item => item.claim_count > 0) ? (
                  <>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                          width={600}
                          height={250}
                          data={totalClaimData.filter(item => item.name !== "Total")}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="claim_type" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="claim_count" fill="#8884d8" name="Claim Count" barSize={35} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <Table
                      dataSource={totalClaimData.filter(item => item.name !== "Total")}
                      columns={columns5}
                      pagination={false}
                      bordered
                      style={{ marginTop: 20 }}
                    />
                  </>
                ) : (
                  <h6 className="report-error" style={{ textAlign: "left", marginTop: 20 }}>
                    Record Not Found
                  </h6>
                )}
              </Card>
            <Card>
            <h5>Claim Paid Vs Outstanding</h5>
            {Array.isArray(ClaimPaidData) && ClaimPaidData.length > 0 && ClaimPaidData.some(item => item.total_amount > 0) ? (
              <>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={ClaimPaidData}
                      layout="vertical"
                      margin={{ top: 20, bottom: 20 }}
                    >
                      <CartesianGrid stroke="#f0f0f0" strokeDasharray="8 4" />
                      <XAxis type="number" tickLine={false} axisLine={false} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tickLine={false}
                        axisLine={false}
                        width={200}
                      />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="total_amount"
                        stroke="#8884d8"
                        strokeWidth={2}
                        dot
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <Table
                  dataSource={ClaimPaidData}
                  columns={columns1}
                  pagination={false}
                  bordered
                  style={{ marginTop: 20 }}
                />
              </>
            ) : (
              <h6 className="report-error" style={{ textAlign: "left", marginTop: 20 }}>
                Record Not Found
              </h6>
            )}
          </Card>

             <Card>
                <h5>Gender Wise</h5>
                {genderWiseData.length > 0 && genderWiseData.some(item => item.count > 0) ? (
                  <>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                      <ResponsiveContainer width={800} height={400}>
                        <PieChart>
                          <Pie
                            data={genderWiseData.filter(item => item.name !== "Total")}
                            dataKey={(entry) => parseFloat(entry.percentage)}
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={160}
                            labelLine={false}
                            label={renderCustomizedLabel1}
                          >
                            {genderWiseData.filter(item => item.name !== "Total").map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS6[index % COLORS6.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend
                            layout="vertical"
                            align="right"
                            verticalAlign="middle"
                            iconSize={10}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    <Table
                      dataSource={genderWiseData.filter(item => item.name !== "Total")}
                      columns={columns6}
                      pagination={false}
                      bordered
                      style={{ marginTop: 20 }}
                    />
                  </>
                ) : (
                  <h6 className="report-error" style={{ textAlign: "left", marginTop: 20 }}>
                    Record Not Found
                  </h6>
                )}
              </Card>
            </Card>
          </>
        ) : null}
      </>
    ),
  }));

  return (
    <Spin spinning={loading}>
      <div className="escalation-matrix-container">
        <div className="escalation-content">
          <Row className="escalation-row-header">
            <span style={{ fontWeight: "700", fontSize: "20px" }}>
              {user?.corporate_name || "Guest"}
            </span>{" "}
            <span style={{ marginLeft: "8px" }}>
              {/* This now uses the 'selectedYear' variable derived from Redux state */}
              (Financial Year: {selectedYear})
            </span>
          </Row>
        </div>
        {policyTypes.length > 0 ? (
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            className="esc-tab"
            items={tabItems}
          />
        ) : (
          <Card style={{ marginTop: "20px" }}>
            <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
              <p style={{ fontSize: "16px", fontWeight: "500" }}>No claim reports data available for {selectedYear}</p>
            </div>
          </Card>
        )}
      </div>
    </Spin>
  );
};

export default ClaimReport;